import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {makeGetRequestToUrl} from './commonFunc.jsx';
import config from '../config/config.js';

const useChunkedRideData = (params) => {
  const [data, setData] = useState([]);  // Holds the cumulative data
  const [loading, setLoading] = useState(false);  // Loading state
  const [error, setError] = useState(null);  // Error state
  const [hasMore, setHasMore] = useState(true);  // To track if more data is available
  const [chunkedNoDriversFound, setChunkedNoDriversFound] = useState(false);
  const [chunkedAutoAssigned, setChunkedAutoAssigned] = useState(false);
  const [requestId, setRequestId] = useState(0);
  const [engagementData, setEngagementData] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [initialRequestId, setInitialRequestId] = useState(0);
  const [chunkedOne, setChunkedOne] = useState(false);
  const [chunkedOneMessage, setChunkedOneMessage] = useState('');
  const hasAlreadyFetched = useRef(false);

  const serverUrl = config.serverUrl;
  const autosUrl = config.autosUrl;

  const navigate = useNavigate();

  const getEngagementDetails = async () => {
    try {
        const token = localStorage.getItem('token');
        const timestamp = new Date().getTime();

        const url = `${autosUrl}/admin/get_engagement_details`;
        const obj={
            token: token, is_panel_request: Number(1), request_id: Number(requestId)
        }
        const response =  await makeGetRequestToUrl(url, obj);
        if(response.flag==200){
          setEngagementData(response.data.engagement_details[0]);
        }
    } catch (error) {
        console.error('Error fetching data:', error);
    }
};

  useEffect(()=> {
    if(requestId!=0)
    getEngagementDetails();
  }, [requestId]);

  useEffect(() => {

    // Don't fetch data if already loading, no more data to fetch, params are invalid, or the API has already been called
    if (
        hasAlreadyFetched.current ||
        !hasMore || 
        loading || 
        !params || 
        Object.keys(params).length === 0 || 
        params.drop_latitude === undefined || 
        params.drop_latitude === null || 
        hasFetched
    ) {
        console.log('Skipping API call due to invalid conditions or duplicate fetch');
        return;
    }

    const fetchData = async () => {
        setLoading(true);
        hasAlreadyFetched.current = true;

        try {
            // Use fetch API for streaming capability
            const controller = new AbortController();
            const signal = controller.signal;
            
            console.log('Sending request to API with params:', params);
            
            // Start the fetch request with streaming enabled
            const response = await fetch(`${autosUrl}/customer/request_ride`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(params),
                signal,
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            // Get a reader from the response body stream
            const reader = response.body.getReader();
            let receivedData = '';
            let requestIdExtracted = false;
            
            // Process the stream as chunks arrive
            while (true) {
                const { done, value } = await reader.read();
                
                if (done) {
                    console.log('Stream complete');
                    break;
                }
                
                // Convert the chunk to text and append to our accumulated data
                const chunk = new TextDecoder().decode(value, { stream: true });
                receivedData += chunk;
                console.log('Received raw chunk:', chunk);
                
                // Try to parse the chunk as JSON if it looks like a complete JSON object
                try {
                    if (chunk.trim().startsWith('{') && chunk.trim().endsWith('}')) {
                        const parsedChunk = JSON.parse(chunk);
                        console.log('Parsed chunk:', parsedChunk);
                        
                        // If this chunk has the request_id, immediately extract and save it
                        if (parsedChunk.flag === 200 && parsedChunk.data && parsedChunk.data.request_id) {
                            console.log('Got request_id immediately from parsed chunk:', parsedChunk.data.request_id);
                            setInitialRequestId(parsedChunk.data.request_id);
                            localStorage.setItem('current_request_id', parsedChunk.data.request_id.toString());
                            requestIdExtracted = true;
                        }

                        if(parsedChunk.flag === 111){
                            console.log('parsedChunk.message', parsedChunk.message);
                            setChunkedOne(true);
                            setChunkedOneMessage(parsedChunk.message);
                        }

                        if(parsedChunk.flag === 116){
                            console.log('parsedChunk.message', parsedChunk.message);
                            setChunkedOne(true);
                            setChunkedOneMessage(parsedChunk.message);
                        }
                    }
                } catch (err) {
                    // Ignore parsing errors for incomplete chunks
                    console.log('Chunk is not a complete JSON object');
                }
                
                // As a fallback, also try to extract the request_id from the accumulated data
                if (!requestIdExtracted && receivedData.includes('"flag":200')) {
                    try {
                        // Find a complete JSON object in the accumulated data
                        const match = receivedData.match(/\{[^\{\}]*"flag":200[^\{\}]*\}/);
                        if (match) {
                            const jsonObject = JSON.parse(match[0]);
                            console.log('Extracted first JSON object:', jsonObject);
                            
                            if (jsonObject.data && jsonObject.data.request_id) {
                                console.log('Got request_id immediately:', jsonObject.data.request_id);
                                setInitialRequestId(jsonObject.data.request_id);
                                localStorage.setItem('current_request_id', jsonObject.data.request_id.toString());
                                requestIdExtracted = true;
                            }
                        }
                    } catch (parseError) {
                        console.log('Error parsing partial data, will try with next chunk', parseError);
                    }
                }
            }
            
            // Process the complete response
            console.log('Processing complete response data');
            const responseString = receivedData;
            
            const responses = responseString.split('}{').map((item, index, array) => {
                if (index === 0) return JSON.parse(item + '}');
                if (index === array.length - 1) return JSON.parse('{' + item);
                return JSON.parse('{' + item + '}');
            });

            for (let i = 0; i < responses.length; i++) {
                if (responses[i].flag === 200) {
                    setInitialRequestId(responses[i].data.request_id);
                }
                if (responses[i].flag === 600) {
                    setChunkedNoDriversFound(true);
                }
                if (responses[i].flag === 601) {
                    setChunkedAutoAssigned(true);
                    setRequestId(responses[i].data.request_id);
                }
            }
            setHasFetched(true); // Mark as fetched to prevent re-fetch
        } catch (err) {
            console.error('Error during API call:', err.message);
            setError(err.message || 'Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    // if(params.user_id != undefined) fetchData();
    const canRequest = params.user_id != undefined || params.merchant_id != undefined;

    if (canRequest) {
        fetchData();
    } else {
        console.log('Skipping request_ride: neither user_id nor merchant_id present', params);
    }
}, [hasMore, loading, params, hasFetched]);

  return { chunkedNoDriversFound, loading, chunkedAutoAssigned, engagementData, initialRequestId, chunkedOne, chunkedOneMessage };
};

export default useChunkedRideData;